import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  ArrowLeft, Sparkles, Loader2, AlertTriangle, Shield,
  CheckCircle, ChevronDown, ChevronUp,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiPost } from '@/lib/api'
import { cn, PRIORITY_LABELS, PRIORITY_COLORS } from '@/lib/utils'

interface TriageResult {
  title: string
  priority: string
  type: string
  assetCategoryCode: string
  assetCategoryReason: string
  suggestedSkills: string[]
  safetyRisk: boolean
  safetyNotes?: string
  confidence: number
  reasoning: string
  _meta?: { provider: string; model: string }
}

export function CreateWorkOrderPage() {
  const navigate = useNavigate()
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId

  // Form state
  const [description, setDescription] = useState('')
  const [location, setLocation] = useState('')
  const [roomNumber, setRoomNumber] = useState('')
  const [guestName, setGuestName] = useState('')
  const [guestVipLevel, setGuestVipLevel] = useState('')

  // Triage state
  const [triage, setTriage] = useState<TriageResult | null>(null)
  const [showReasoning, setShowReasoning] = useState(false)

  // Override state (user can adjust AI suggestions)
  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState('')
  const [type, setType] = useState('')

  // ── Triage mutation ───────────────────────────────────────────────────
  const triageMutation = useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiPost<TriageResult>('/ai/triage', body),
    onSuccess: (data) => {
      setTriage(data)
      setTitle(data.title)
      setPriority(data.priority)
      setType(data.type)
    },
  })

  // ── Create WO mutation ────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/properties/${pid}/work-orders`, body),
    onSuccess: (data: any) => {
      navigate(`/work-orders/${data.id}`)
    },
  })

  function handleTriage() {
    if (!description.trim()) return
    triageMutation.mutate({
      description: description.trim(),
      ...(location && { location }),
      ...(roomNumber && { roomNumber }),
      ...(guestName && { guestName }),
      ...(guestVipLevel && { guestVipLevel }),
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return

    // Build safety/AI notes to stamp on the work order
    const notesParts: string[] = []
    if (triage?.safetyRisk) {
      notesParts.push(`⚠ SAFETY WARNING: ${triage.safetyNotes || 'Safety risk identified by AI triage'}`)
    }
    if (triage) {
      notesParts.push(`AI Triage (${triage._meta?.provider ?? 'ai'}, ${Math.round(triage.confidence * 100)}% confidence): Category: ${triage.assetCategoryCode} — ${triage.assetCategoryReason}. Skills: ${triage.suggestedSkills.join(', ')}.`)
    }

    createMutation.mutate({
      title: title.trim(),
      description: description.trim(),
      priority: priority || 'STANDARD',
      type: type || 'REACTIVE',
      ...(location && { location }),
      ...(roomNumber && { roomNumber }),
      ...(guestName && { pmsGuestName: guestName }),
      ...(guestVipLevel && { pmsGuestVipLevel: guestVipLevel }),
      ...(triage?.faultCode && { faultCode: triage.assetCategoryCode }),
      ...(notesParts.length > 0 && { notes: notesParts.join('\n') }),
    })
  }

  const confidenceColor = triage
    ? triage.confidence >= 0.8 ? 'text-green-600' : triage.confidence >= 0.5 ? 'text-amber-600' : 'text-red-600'
    : ''

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/work-orders')} className="text-gray-400 hover:text-navy-800">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-navy-800">New Work Order</h1>
          <p className="text-gray-500 text-sm mt-0.5">Describe the issue and AI will classify it</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Describe */}
        <div className="card">
          <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-navy-800 text-white text-xs flex items-center justify-center">1</span>
            Describe the Issue
          </h2>

          <div className="space-y-4">
            <div>
              <label className="label">What's the problem? *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="input resize-none"
                placeholder="e.g. Guest in room 412 reports a banging noise from the ceiling and a burning smell..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Location</label>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="input"
                  placeholder="e.g. 4th Floor, East Wing"
                />
              </div>
              <div>
                <label className="label">Room Number</label>
                <input
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value)}
                  className="input"
                  placeholder="e.g. 412"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Guest Name</label>
                <input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="input"
                  placeholder="Leave blank if no guest involved"
                />
              </div>
              <div>
                <label className="label">VIP Level</label>
                <select
                  value={guestVipLevel}
                  onChange={(e) => setGuestVipLevel(e.target.value)}
                  className="input"
                >
                  <option value="">None</option>
                  <option value="GOLD">Gold</option>
                  <option value="PLATINUM">Platinum</option>
                  <option value="DIAMOND">Diamond</option>
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={handleTriage}
              disabled={!description.trim() || triageMutation.isPending}
              className="btn-primary w-full justify-center"
            >
              {triageMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analysing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Classify with AI
                </>
              )}
            </button>

            {triageMutation.isError && (
              <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                AI classification failed. You can still create the work order manually below.
              </div>
            )}
          </div>
        </div>

        {/* Step 2: AI Result + Overrides */}
        {(triage || triageMutation.isError) && (
          <div className="card">
            <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-navy-800 text-white text-xs flex items-center justify-center">2</span>
              Review &amp; Adjust
            </h2>

            {/* AI Triage Summary */}
            {triage && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-navy-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    AI Classification
                  </span>
                  <div className="flex items-center gap-2">
                    {triage._meta && (
                      <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                        {triage._meta.provider}
                      </span>
                    )}
                    <span className={cn('text-sm font-medium', confidenceColor)}>
                      {Math.round(triage.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>

                {triage.safetyRisk && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <Shield className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Safety Risk Identified</p>
                      {triage.safetyNotes && (
                        <p className="text-xs text-red-600 mt-0.5">{triage.safetyNotes}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <span className="badge bg-purple-100 text-purple-800">
                    {triage.assetCategoryCode}
                  </span>
                  {triage.suggestedSkills.map((s) => (
                    <span key={s} className="badge bg-gray-100 text-gray-600">{s}</span>
                  ))}
                </div>

                <p className="text-xs text-gray-500">{triage.assetCategoryReason}</p>

                <button
                  type="button"
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="text-xs text-navy-500 hover:text-navy-800 flex items-center gap-1"
                >
                  {showReasoning ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  AI reasoning
                </button>
                {showReasoning && (
                  <p className="text-xs text-gray-500 bg-white rounded-lg p-3">{triage.reasoning}</p>
                )}
              </div>
            )}

            {/* Editable fields */}
            <div className="space-y-4">
              <div>
                <label className="label">Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input"
                  required
                  placeholder="Work order title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="input"
                  >
                    {['EMERGENCY', 'URGENT', 'HIGH', 'STANDARD', 'LOW', 'PLANNED'].map((p) => (
                      <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                    ))}
                  </select>
                  {triage && priority !== triage.priority && (
                    <p className="text-xs text-amber-600 mt-1">
                      AI suggested: {PRIORITY_LABELS[triage.priority]}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">Type</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="input"
                  >
                    <option value="REACTIVE">Reactive</option>
                    <option value="PPM">PPM</option>
                    <option value="INSPECTION">Inspection</option>
                    <option value="PROJECT">Project</option>
                    <option value="COMPLIANCE">Compliance</option>
                  </select>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!title.trim() || createMutation.isPending}
                className="btn-primary w-full justify-center"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Create Work Order
                  </>
                )}
              </button>

              {createMutation.isError && (
                <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  Failed to create work order. Please try again.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual create without AI */}
        {!triage && !triageMutation.isPending && description.trim() && (
          <p className="text-center text-sm text-gray-400">
            Click "Classify with AI" above, or{' '}
            <button
              type="button"
              onClick={() => {
                setTitle('')
                setPriority('STANDARD')
                setType('REACTIVE')
                setTriage({ title: '', priority: 'STANDARD', type: 'REACTIVE', assetCategoryCode: '', assetCategoryReason: '', suggestedSkills: [], safetyRisk: false, confidence: 0, reasoning: '' })
              }}
              className="text-navy-600 hover:text-navy-800 underline"
            >
              skip AI and fill in manually
            </button>
          </p>
        )}
      </form>
    </div>
  )
}
